import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import { AxiosResponse } from 'axios';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSessionUser from '../../../hooks/useSessionUser';
import { AssessmentSessionProcessTypeId } from '../../../services/exams/assessmentSession/assessments.types';
import { approveExamTaskGroupSolutionScore } from '../../../services/scoringModel/scoringModel.service';
import {
  ApproveExamTaskGroupSolutionScoreResponse,
  SendBackSampleResponse,
} from '../../../services/scoringModel/scoringModel.types';
import { getProcessLabel } from '../../../utils/assessmentSessionProcess';
import { getSampleAssessmentUrl } from '../../../utils/routerUrls';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import { Prompt, SubText, promptType } from '../../Prompt/Prompt.component';
import { useTranslation } from 'react-i18next';
import { getExamTaskWithoutRequiredCommentSubtext } from '../../Assessment/Footer/Helpers.component';

interface ConfirmDenyKKProps {
  id: string;
  isConfirmed: boolean;
  onActionCompleted: () => void;
  sampleAssessmentSelectedProcess?: AssessmentSessionProcessTypeId | null;
  isImageLoadError?: boolean;
}

export const ConfirmDenyKK: React.FC<ConfirmDenyKKProps> = (props) => {
  const { userAssessmentSessionInfo } = useSessionUser();
  const confirmationSucceed = `RGZ: ${props.id} zostało potwierdzone i pomyślnie przypisane do ${
    getProcessLabel(props.sampleAssessmentSelectedProcess ?? undefined).toProcess
  }.`;
  const confirmationFailed = `Zatwierdzenie i przypisanie do ${
    getProcessLabel(props.sampleAssessmentSelectedProcess ?? undefined).toProcess
  } RGZ: ${props.id} nie powiodło sie. Spróbuj ponownie później.`;
  const confirmConfirmation = `Czy na pewno chcesz usunąć zatwierdzenie dla RGZ: ${props.id}?`;
  const confirmationRemovalSucceed = `Usunięcie zatwierdzenia RGZ: ${props.id} zakończone sukcesem.`;
  const confirmationRemovalFailed = `Usunięcie zatwierdzenia RGZ: ${props.id} nie powiodło się. Spróbuj ponownie później.`;

  const [prompt, setPrompt] = useState<{
    isOpen: boolean;
    type: promptType;
    mainText: string;
    subText?: SubText[];
    mainActionButtonText?: string;
    onCancel?: () => void;
    cancelButtonText?: string;
  }>({
    isOpen: false,
    type: 'success',
    mainText: '',
  });

  const [enhancedText, setEnhancedText] = useState<JSX.Element>(<div></div>);

  const history = useHistory();
  const { t } = useTranslation();

  const displayErrorPrompt = (confirm: boolean) => {
    setPrompt({
      isOpen: true,
      type: 'error',
      mainText: confirm ? confirmationFailed : confirmationRemovalFailed,
      mainActionButtonText: 'POWRÓT',
    });
    setEnhancedText(<div></div>);
  };

  const handleOnCancel = () => {
    setPrompt((prev) => ({ ...prev, isOpen: false }));
    props.onActionCompleted();
    history.push(getSampleAssessmentUrl(userAssessmentSessionInfo.assessmentSessionId));
  };

  const handleButtonClick = (confirm: boolean) => {
    approveExamTaskGroupSolutionScore(props.id, confirm)
      .then((res: AxiosResponse<SendBackSampleResponse>) => {
        if (res.data.result === ApproveExamTaskGroupSolutionScoreResponse.Success) {
          setPrompt({
            isOpen: true,
            type: 'success',
            mainText: confirm ? confirmationSucceed : confirmationRemovalSucceed,
            mainActionButtonText: confirm ? 'POWRÓT DO RGZ' : undefined,
            cancelButtonText: confirm ? 'POWRÓT DO LISTY' : 'POWRÓT',
            onCancel: handleOnCancel,
          });
          setEnhancedText(<div></div>);
          props.onActionCompleted();
        } else if (res.data.result === ApproveExamTaskGroupSolutionScoreResponse.CriteriaCommentRequired) {
          const subText = getExamTaskWithoutRequiredCommentSubtext(res.data.examTaskWithoutRequiredComment);
          setEnhancedText(subText);
          setPrompt({
            isOpen: true,
            type: 'error',
            mainText: 'Nie można zatwierdzić RGZ, ponieważ nie został wprowadzony komentarz do:',
            mainActionButtonText: confirm ? 'POWRÓT DO RGZ' : undefined,
            cancelButtonText: confirm ? 'POWRÓT DO LISTY' : 'POWRÓT',
            onCancel: handleOnCancel,
          });
        } else {
          displayErrorPrompt(confirm);
        }
      })
      .catch(() => {
        displayErrorPrompt(confirm);
      });
  };

  const successConfirmHandle = () => {
    setPrompt((prev) => ({ ...prev, isOpen: false }));
  };

  const handleOnConfirm = () => {
    prompt.type === 'confirmation' ? handleButtonClick(false) : successConfirmHandle();
  };

  const handleConfirmationRemoval = () => {
    setPrompt({
      isOpen: true,
      type: 'confirmation',
      mainText: confirmConfirmation,
      mainActionButtonText: 'TAK',
      cancelButtonText: 'Anuluj',
      onCancel: handleOnCancel,
    });
  };

  const label = props.isConfirmed ? 'Usuń zatwierdzenie' : 'Zatwierdź';
  const color = props.isConfirmed ? '#BC0000' : '#347F42';
  const icon = props.isConfirmed ? <ClearIcon /> : <DoneIcon />;

  return (
    <>
      <ButtonOne
        aria-label={label}
        variant="outlined"
        size="small"
        startIcon={icon}
        disabled={(!props.isConfirmed && props.sampleAssessmentSelectedProcess === null) || props.isImageLoadError}
        style={{
          marginLeft: '12px',
          color: color,
          borderColor: color,
        }}
        onClick={() => (props.isConfirmed ? handleConfirmationRemoval() : handleButtonClick(true))}
      >
        {label}
      </ButtonOne>
      <Prompt
        type={prompt.type}
        open={prompt.isOpen}
        mainText={prompt.mainText}
        mainActionButtonText={prompt.mainActionButtonText}
        subTexts={prompt.subText}
        enhancedText={enhancedText}
        onConfirm={handleOnConfirm}
        cancelButtonText={prompt.cancelButtonText}
        onCancel={prompt.onCancel}
      />
    </>
  );
};
